import type { Story } from "inkjs";
import { EventBus } from "../EventBus.js";
import { WorldStore } from "../../store/WorldStore.js";

// ── Static observed variables ─────────────────────────────────────────────────

/**
 * Ink variables that every adventure story must declare (via `VAR`) and that
 * NarrativeManager mirrors into SessionStore automatically on story load.
 *
 * Adventure-specific flags (e.g. `has_key`, `quest_*`) are observed dynamically
 * via `NarrativeManager.observeVariable()`.
 */
export const INK_OBSERVED_VARS: readonly string[] = ["boss_dead", "hostile", "shop_open"] as const;

// ── Skill-check resolver (N04) ────────────────────────────────────────────────

/**
 * `roll_skill(skill, dc)` needs a synchronous truthy/falsy return so Ink can
 * branch on the result the same turn. We can't pump that through EventBus
 * (asynchronous, no return value), so NarrativeManager registers a resolver
 * up-front and `roll_skill` calls it directly.
 *
 * When no resolver is installed (early boot, unit-test harnesses that only
 * load the contract), `roll_skill` falls back to emitting
 * `narrative:skill_check_requested` and returning `0` so the old stub
 * behaviour is preserved.
 */
export type SkillCheckResolver = (skill: string, dc: number) => boolean;

let skillCheckResolver: SkillCheckResolver | null = null;

export function setSkillCheckResolver(resolver: SkillCheckResolver | null): void {
  skillCheckResolver = resolver;
}

// ── External function stubs ───────────────────────────────────────────────────

/**
 * d20 + speaker-stat skill check vs DC. When NarrativeManager has installed
 * a resolver (normal game path), defer to it and return the boolean result
 * to Ink. Before the resolver is installed we keep the legacy stub that
 * emits `narrative:skill_check_requested` and returns 0.
 */
function roll_skill(skill: unknown, dc: unknown): boolean | number {
  const skillStr = String(skill);
  const dcNum = Number(dc);
  if (skillCheckResolver) {
    return skillCheckResolver(skillStr, dcNum);
  }
  EventBus.emit("narrative:skill_check_requested", { skill: skillStr, dc: dcNum });
  return 0;
}

/**
 * Add item(s) to the party inventory.
 * Stub: emits `inventory:give_item`; future InventoryStore subscribes.
 */
function give_item(item_id: unknown, qty: unknown): void {
  EventBus.emit("inventory:give_item", {
    itemId: String(item_id),
    qty: Number(qty),
  });
}

/**
 * Open the shop UI for `npc_id`.
 * Stub: emits `narrative:open_shop`; future shop-UI system subscribes.
 */
function open_shop(npc_id: unknown): void {
  EventBus.emit("narrative:open_shop", { npcId: String(npc_id) });
}

/**
 * Flip an NPC's faction to hostile and trigger combat.
 * Stub: emits `enemy:set_hostile`; future EnemyManager subscribes.
 */
function set_hostile(npc_id: unknown): void {
  EventBus.emit("enemy:set_hostile", { npcId: String(npc_id) });
}

/**
 * Play a one-off bark line outside the normal dialogue flow.
 * Stub: emits `narrative:bark`; future feed/log system subscribes.
 */
function play_bark(line: unknown): void {
  EventBus.emit("narrative:bark", { line: String(line) });
}

/**
 * Award XP to the party.
 * Stub: emits `world:give_xp`; future progression system subscribes.
 */
function give_xp(amount: unknown): void {
  EventBus.emit("world:give_xp", { amount: Number(amount) });
}

/**
 * Toggle an authored ceiling light (W22).
 * Flips membership in `WorldStore.lightsOff`, which emits
 * `world:light_toggled`; `LightManager` flips the `PointLight.visible`
 * flag in place. Unknown ids are silently no-ops so a cutscene can
 * ship before its art does.
 */
function toggle_light(light_id: unknown): void {
  WorldStore.toggleLight(String(light_id));
}

/**
 * Trigger an autosave checkpoint (Ink-driven).
 * Stub: emits `save:autosave_requested`; future SaveManager subscribes.
 */
function autosave(): void {
  EventBus.emit("save:autosave_requested", {});
}

// ── Registry ──────────────────────────────────────────────────────────────────

/** All external function names this contract exposes to every Ink story. */
export const INK_EXTERNAL_FUNCTIONS = [
  "roll_skill",
  "give_item",
  "open_shop",
  "set_hostile",
  "play_bark",
  "give_xp",
  "autosave",
  "toggle_light",
] as const;

const FUNCTION_MAP: Record<string, (...args: unknown[]) => unknown> = {
  roll_skill,
  give_item,
  open_shop,
  set_hostile,
  play_bark,
  give_xp,
  autosave,
  toggle_light,
};

/**
 * Bind all contract external functions to `story`.
 * Call once after `loadInkStory`, before any `story.Continue()` calls.
 * All functions are marked lookahead-safe (no side-effects during lookahead).
 */
export function bindContractToStory(story: Story): void {
  for (const name of INK_EXTERNAL_FUNCTIONS) {
    story.BindExternalFunction(name, FUNCTION_MAP[name]!, true);
  }
}
